import React, { FC } from 'react';
import { GetServerSidePropsContext } from 'next';
import { TypeHomePage, TypePage } from '@/core/generated-types';
import { loadLayout } from '@/utils/loadLayout';
import {
  ContentfulPreviewData,
  getEntries,
  getEntriesByPageType,
} from '@/lib/contentful';
import {
  CONTENT_GROUP_REFERRAL_PAGE,
  CONTENT_TYPE_PAGE,
  CONTENT_TYPE_REFERRAL_PAGE,
} from '@/core/constants';
import { getSSRSafeAuthenticatedUser, setCustomCookie } from '@/utils/helpers';
import { ReferralLayout } from '@/components/layouts/pages/ReferralLayout';
import { getUser } from '@/lib/api/accountManager/apis/getUser';
import { TypeResponseSuccess } from '@/lib/api/config';
import { TypeUserRecord } from '@/lib/api/accountManager/types';

export interface ReferAFriendProps {
  title: string;
  page: TypeHomePage;
  user: TypeUserRecord;
}

function getRedirectPath(locale?: string): string {
  switch (locale) {
    case 'nl':
      return '/nl/pages/refer-a-friend';
    case 'fr':
      return '/fr/pages/refer-a-friend';
    case 'de':
      return '/de/pages/refer-a-friend';
    case 'es':
      return '/es/pages/refer-a-friend';
    case 'en-US':
    case 'en-AU':
    case 'en-GB':
    case 'en-EU':
    default:
      return '/pages/refer-a-friend';
  }
}

export const getServerSideProps = async (
  context: GetServerSidePropsContext,
) => {
  const { params, preview, previewData } = context;
  const locale = (params?.locale as string) || 'en-US';
  const previewDataPayload = previewData as ContentfulPreviewData;
  const { authToken, userUuid } = await getSSRSafeAuthenticatedUser(context);
  let entry;
  let userResponseSuccess;

  if (!authToken || !userUuid) {
    const page = await getEntriesByPageType(
      {
        'fields.slug': context.params?.slug as string,
        locale,
        'fields.contentGroup': CONTENT_GROUP_REFERRAL_PAGE.LANDING_PAGE,
      },
      CONTENT_TYPE_PAGE,
      preview,
      previewDataPayload,
    );
    entry = page?.[0].items?.[0];
  } else {
    const userResponse = await getUser({ authToken, userUuid });
    userResponseSuccess = userResponse as TypeResponseSuccess<TypeUserRecord>;
    const page = await getEntries(
      {
        'fields.slug': context.params?.slug as string,
        locale,
        contentType: CONTENT_TYPE_REFERRAL_PAGE.REFERRAL_PAGE,
      },
      preview,
      previewDataPayload,
    );
    entry = page?.items?.[0];
  }

  if (!authToken || !userUuid) {
    setCustomCookie(context, 'returnTo', getRedirectPath(locale));
  }

  const { header, footer, footerLegal } = await loadLayout(
    locale,
    preview,
    previewDataPayload,
  );

  if (!entry) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      page: entry || null,
      header,
      footer,
      footerLegal,
      user: userResponseSuccess?.data || null,
    },
  };
};

const ReferAFriend: FC<ReferAFriendProps> = ({ page, user }) => {
  return <ReferralLayout page={page as TypePage} user={user} />;
};

export default ReferAFriend;
